// Typescript CodeGen Prototype "Expectant Sunshine"
// Transform Version: v1.0.0
// Template Version:  v1.0.0
//
// -- DO NOT MODIFY THIS FILE! --

/* tslint:disable:no-trailing-whitespace */

import * as _ from 'lodash';
import * as Wellspring from '@greenheck/wellspring';
import * as Axon from '@jlekie/axon';
import { v4 as Uuid } from 'uuid';
import * as ParseHelpers from '@jlekie/parse-helpers';
import * as Zod from 'zod';
import * as ModelContract from './configuration.modelContract';
import * as ModelMapping from './configuration.modelMapping';

export enum ConstraintEngineTypes {
    Configit,
    Mapper,
    Reflection,
    FanSelection,
    Macro,
    Recommendation,
    Static,
    AssignedValue
}

export function isConstraintModelModel(source: any): source is IConstraintModel {
    // Method Implementation: simple
    return source.modelName === 'ConstraintModel';
}

export function isEngineModel(source: any): source is IEngine {
    // Method Implementation: simple
    return source.modelName === 'Engine';
}

export function isConfigitEngineModel(source: any): source is IConfigitEngine {
    // Method Implementation: simple
    return source.modelName === 'ConfigitEngine';
}

export function isConfigitEngineDependencyMappingModel(source: any): source is IConfigitEngineDependencyMapping {
    // Method Implementation: simple
    return source.modelName === 'ConfigitEngineDependencyMapping';
}

export function isMapperEngineModel(source: any): source is IMapperEngine {
    // Method Implementation: simple
    return source.modelName === 'MapperEngine';
}

export function isReflectionEngineModel(source: any): source is IReflectionEngine {
    // Method Implementation: simple
    return source.modelName === 'ReflectionEngine';
}

export function isFanSelectionEngineModel(source: any): source is IFanSelectionEngine {
    // Method Implementation: simple
    return source.modelName === 'FanSelectionEngine';
}

export function isMacroEngineModel(source: any): source is IMacroEngine {
    // Method Implementation: simple
    return source.modelName === 'MacroEngine';
}

export function isMacroEngineValueModel(source: any): source is IMacroEngineValue {
    // Method Implementation: simple
    return source.modelName === 'MacroEngineValue';
}

export function isRecommendationEngineModel(source: any): source is IRecommendationEngine {
    // Method Implementation: simple
    return source.modelName === 'RecommendationEngine';
}

export function isStaticEngineModel(source: any): source is IStaticEngine {
    // Method Implementation: simple
    return source.modelName === 'StaticEngine';
}

export function isAssignedValueEngineModel(source: any): source is IAssignedValueEngine {
    // Method Implementation: simple
    return source.modelName === 'AssignedValueEngine';
}

export interface ConstraintModelZodSchemaShape {
    engine?: EngineZodSchemaShape;

    contracts?: ModelContract.ContractZodSchemaShape[];

    mapper?: ModelMapping.MapperZodSchemaShape;
}

export interface EngineZodSchemaShape {
    engineType?: string;
}

export interface ConfigitEngineZodSchemaShape {
    engineType?: string;

    vtzUri?: string;

    dependencyMappings?: ConfigitEngineDependencyMappingZodSchemaShape[];
}

export interface ConfigitEngineDependencyMappingZodSchemaShape {
    variable?: string;

    dependentVariables?: string[];
}

export interface MapperEngineZodSchemaShape {
    engineType?: string;

    parentContract?: ModelContract.ContractZodSchemaShape;

    decouple?: string;

    decoupled?: boolean;

    decoupledValues?: any;
}

export interface ReflectionEngineZodSchemaShape {
    engineType?: string;

    type?: string;
}

export interface FanSelectionEngineZodSchemaShape {
    engineType?: string;
}

export interface MacroEngineZodSchemaShape {
    engineType?: string;

    values?: MacroEngineValueZodSchemaShape[];
}

export interface MacroEngineValueZodSchemaShape {
    name?: string;

    value?: string;
}

export interface RecommendationEngineZodSchemaShape {
    engineType?: string;
}

export interface StaticEngineZodSchemaShape {
    engineType?: string;

    values?: any;
}

export interface AssignedValueEngineZodSchemaShape {
    engineType?: string;

    variables?: string[];
}

// Artifact Implementation: artifacts/zodSchemaType
export const ConstraintModelZodSchema: Zod.ZodSchema<ConstraintModelZodSchemaShape> = Zod.lazy(() => Zod.object({
    engine: EngineZodSchema.optional(),
    contracts: Zod.array(ModelContract.ContractZodSchema).optional(),
    mapper: ModelMapping.MapperZodSchema.optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const EngineZodSchema: Zod.ZodSchema<EngineZodSchemaShape> = Zod.lazy(() => Zod.object({
    engineType: Zod.string()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const ConfigitEngineZodSchema: Zod.ZodSchema<ConfigitEngineZodSchemaShape> = Zod.lazy(() => Zod.object({
    engineType: Zod.string(),
    vtzUri: Zod.string().optional(),
    dependencyMappings: Zod.array(ConfigitEngineDependencyMappingZodSchema).optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const ConfigitEngineDependencyMappingZodSchema: Zod.ZodSchema<ConfigitEngineDependencyMappingZodSchemaShape> = Zod.lazy(() => Zod.object({
    variable: Zod.string().optional(),
    dependentVariables: Zod.array(Zod.string()).optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const MapperEngineZodSchema: Zod.ZodSchema<MapperEngineZodSchemaShape> = Zod.lazy(() => Zod.object({
    engineType: Zod.string(),
    parentContract: ModelContract.ContractZodSchema.optional(),
    decouple: Zod.string().optional(),
    decoupled: Zod.boolean().optional(),
    decoupledValues: Zod.any().optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const ReflectionEngineZodSchema: Zod.ZodSchema<ReflectionEngineZodSchemaShape> = Zod.lazy(() => Zod.object({
    engineType: Zod.string(),
    type: Zod.string().optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const FanSelectionEngineZodSchema: Zod.ZodSchema<FanSelectionEngineZodSchemaShape> = Zod.lazy(() => Zod.object({
    engineType: Zod.string()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const MacroEngineZodSchema: Zod.ZodSchema<MacroEngineZodSchemaShape> = Zod.lazy(() => Zod.object({
    engineType: Zod.string(),
    values: Zod.array(MacroEngineValueZodSchema).optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const MacroEngineValueZodSchema: Zod.ZodSchema<MacroEngineValueZodSchemaShape> = Zod.lazy(() => Zod.object({
    name: Zod.string().optional(),
    value: Zod.string().optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const RecommendationEngineZodSchema: Zod.ZodSchema<RecommendationEngineZodSchemaShape> = Zod.lazy(() => Zod.object({
    engineType: Zod.string()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const StaticEngineZodSchema: Zod.ZodSchema<StaticEngineZodSchemaShape> = Zod.lazy(() => Zod.object({
    engineType: Zod.string(),
    values: Zod.any().optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const AssignedValueEngineZodSchema: Zod.ZodSchema<AssignedValueEngineZodSchemaShape> = Zod.lazy(() => Zod.object({
    engineType: Zod.string(),
    variables: Zod.array(Zod.string()).optional()
}).nonstrict());

export interface IConstraintModel extends Wellspring.IModel {
    readonly modelName: string;

    readonly engine: AEngine;

    readonly contracts: ModelContract.Contract[];

    readonly mapper: ModelMapping.Mapper;

    readonly isEngineDefined: boolean;

    readonly isContractsDefined: boolean;

    readonly isMapperDefined: boolean;
}

export interface IEngine extends Wellspring.IModel {
    readonly modelName: string;
}

export interface IConfigitEngine extends IEngine, Wellspring.IModel {
    readonly modelName: string;

    readonly vtzUri: string;

    readonly dependencyMappings: ConfigitEngineDependencyMapping[];

    readonly isVtzUriDefined: boolean;

    readonly isDependencyMappingsDefined: boolean;
}

export interface IConfigitEngineDependencyMapping extends Wellspring.IModel {
    readonly modelName: string;

    readonly variable: string;

    readonly dependentVariables: string[];

    readonly isVariableDefined: boolean;

    readonly isDependentVariablesDefined: boolean;
}

export interface IMapperEngine extends IEngine, Wellspring.IModel {
    readonly modelName: string;

    readonly parentContract: ModelContract.Contract;

    readonly decouple: string;

    readonly decoupled: boolean;

    readonly decoupledValues: Record<string, string>;

    readonly isParentContractDefined: boolean;

    readonly isDecoupleDefined: boolean;

    readonly isDecoupledDefined: boolean;

    readonly isDecoupledValuesDefined: boolean;
}

export interface IReflectionEngine extends IEngine, Wellspring.IModel {
    readonly modelName: string;

    readonly type: string;

    readonly isTypeDefined: boolean;
}

export interface IFanSelectionEngine extends IEngine, Wellspring.IModel {
    readonly modelName: string;
}

export interface IMacroEngine extends IEngine, Wellspring.IModel {
    readonly modelName: string;

    readonly values: MacroEngineValue[];

    readonly isValuesDefined: boolean;
}

export interface IMacroEngineValue extends Wellspring.IModel {
    readonly modelName: string;

    readonly name: string;

    readonly value: string;

    readonly isNameDefined: boolean;

    readonly isValueDefined: boolean;
}

export interface IRecommendationEngine extends IEngine, Wellspring.IModel {
    readonly modelName: string;
}

export interface IStaticEngine extends IEngine, Wellspring.IModel {
    readonly modelName: string;

    readonly values: Record<string, string>;

    readonly isValuesDefined: boolean;
}

export interface IAssignedValueEngine extends IEngine, Wellspring.IModel {
    readonly modelName: string;

    readonly variables: string[];

    readonly isVariablesDefined: boolean;
}

export class ConstraintModel implements IConstraintModel {
    public static readonly modelName: string = 'ConstraintModel';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'engine', 'contracts', 'mapper' ];

    public get modelName(): string {
        // Method Implementation: simple
        return ConstraintModel.modelName;
    }

    private _engine: AEngine;

    private _contracts: ModelContract.Contract[];

    private _mapper: ModelMapping.Mapper;

    public get engine(): AEngine {
        // Method Implementation: simple
        return this._engine;
    }

    public get contracts(): ModelContract.Contract[] {
        // Method Implementation: simple
        return this._contracts;
    }

    public get mapper(): ModelMapping.Mapper {
        // Method Implementation: simple
        return this._mapper;
    }

    public set engine(value: AEngine) {
        // Method Implementation: simple
        this._engine = value;
        this._isEngineDefined = true;
    }

    public set contracts(value: ModelContract.Contract[]) {
        // Method Implementation: simple
        this._contracts = value;
        this._isContractsDefined = true;
    }

    public set mapper(value: ModelMapping.Mapper) {
        // Method Implementation: simple
        this._mapper = value;
        this._isMapperDefined = true;
    }

    private _isEngineDefined: boolean;

    private _isContractsDefined: boolean;

    private _isMapperDefined: boolean;

    public get isEngineDefined(): boolean {
        // Method Implementation: simple
        return this._isEngineDefined;
    }

    public get isContractsDefined(): boolean {
        // Method Implementation: simple
        return this._isContractsDefined;
    }

    public get isMapperDefined(): boolean {
        // Method Implementation: simple
        return this._isMapperDefined;
    }

    public constructor();
    public constructor(source: IConstraintModel);
    public constructor(source: Partial<Pick<ConstraintModel, 'engine' | 'contracts' | 'mapper'>>);
    public constructor(source?: IConstraintModel | Partial<Pick<ConstraintModel, 'engine' | 'contracts' | 'mapper'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            if (isConstraintModelModel(source)) {
                if (source.isEngineDefined) {
                    this._engine = source.engine;
                    this._isEngineDefined = true;
                }
                else {
                    this._engine = undefined as any;
                    this._isEngineDefined = false;
                }
                
                if (source.isContractsDefined) {
                    this._contracts = source.contracts;
                    this._isContractsDefined = true;
                }
                else {
                    this._contracts = [];
                    this._isContractsDefined = true;
                }
                
                if (source.isMapperDefined) {
                    this._mapper = source.mapper;
                    this._isMapperDefined = true;
                }
                else {
                    this._mapper = new ModelMapping.Mapper();
                    this._isMapperDefined = true;
                }
            }
            else {
                if (source.engine !== undefined) {
                    this._engine = source.engine;
                    this._isEngineDefined = true;
                }
                else {
                    this._engine = undefined as any;
                    this._isEngineDefined = false;
                }
                
                if (source.contracts !== undefined) {
                    this._contracts = source.contracts;
                    this._isContractsDefined = true;
                }
                else {
                    this._contracts = [];
                    this._isContractsDefined = true;
                }
                
                if (source.mapper !== undefined) {
                    this._mapper = source.mapper;
                    this._isMapperDefined = true;
                }
                else {
                    this._mapper = new ModelMapping.Mapper();
                    this._isMapperDefined = true;
                }
            }
        }
        else {
            this._engine = undefined as any;
            this._isEngineDefined = false;
        
            this._contracts = [];
            this._isContractsDefined = true;
        
            this._mapper = new ModelMapping.Mapper();
            this._isMapperDefined = true;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return ConstraintModel.propertyNames;
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [];
        
        if (this.isEngineDefined && this.engine !== undefined)
            definedProperties.push('engine');
        if (this.isContractsDefined && this.contracts !== undefined)
            definedProperties.push('contracts');
        if (this.isMapperDefined && this.mapper !== undefined)
            definedProperties.push('mapper');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'engine':
                return this.engine;
            case 'contracts':
                return this.contracts;
            case 'mapper':
                return this.mapper;
            default:
                throw new Error(`Property ${propertyName} not recognized`);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        if (this.isEngineDefined && this.engine !== undefined) {
            if (this.engine !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('engine', 'model'));
        
                // Implementation: axon/write/model
                writer.writeModelHeader(new Axon.ModelHeader(this.engine.getModelName(), this.engine.getDefinedPropertyNames().length));
                this.engine.write(writer);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('engine', 'null'));
            }
        }
        
        if (this.isContractsDefined && this.contracts !== undefined) {
            if (this.contracts !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('contracts', 'array'));
        
                // Implementation: axon/write/array
                writer.writeArrayHeader(new Axon.ArrayHeader(this.contracts.length));
                for (let i = 0; i < this.contracts.length; i++) {
                    const item = this.contracts[i];
                
                    if (item !== null) {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('model'));
                
                        // Implementation: axon/write/model
                        writer.writeModelHeader(new Axon.ModelHeader(item.getModelName(), item.getDefinedPropertyNames().length));
                        item.write(writer);
                    }
                    else {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('null'));
                    }
                }
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('contracts', 'null'));
            }
        }
        
        if (this.isMapperDefined && this.mapper !== undefined) {
            if (this.mapper !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('mapper', 'model'));
        
                // Implementation: axon/write/model
                writer.writeModelHeader(new Axon.ModelHeader(this.mapper.getModelName(), this.mapper.getDefinedPropertyNames().length));
                this.mapper.write(writer);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('mapper', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'engine':
                // Implementation: axon/readProperty/abstractModel
                if (propertyHeader.type === 'model') {
                    const modelHeader = reader.readModelHeader();
                
                    this.engine = <any>ModelClassFactory.createModel(modelHeader.modelName);
                    this.engine.read(reader, modelHeader);
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'engine' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'engine'`);
                }
        
                return true;
            case 'contracts':
                // Implementation: axon/readProperty/array
                if (propertyHeader.type === 'array') {
                    const arrayHeader = reader.readArrayHeader();
                
                    this.contracts = [];
                
                    let item: ModelContract.Contract = <any>undefined;
                    for (let i = 0; i < arrayHeader.itemCount; i++) {
                        const arrayItemHeader = reader.readArrayItemHeader();
                
                        // Implementation: model
                        if (arrayItemHeader.type === 'model') {
                            const modelHeader = reader.readModelHeader();
                        
                            if (modelHeader.modelName === ModelContract.Contract.modelName) {
                                item = new ModelContract.Contract();
                                item.read(reader, modelHeader);
                            }
                            else {
                                throw new Error(`Unexpected model type '${modelHeader.modelName}'; expected '${ModelContract.Contract.modelName}'`);
                            }
                        }
                        else if (arrayItemHeader.type === 'null') {
                            throw new Error(`Value for property 'contracts' cannot be null`);
                        }
                        else {
                            throw new Error(`Unexpected data type '${arrayItemHeader.type}' for property 'contracts'`);
                        }
                
                        this.contracts.push(item);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'contracts' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'contracts'`);
                }
        
                return true;
            case 'mapper':
                // Implementation: axon/readProperty/model
                if (propertyHeader.type === 'model') {
                    const modelHeader = reader.readModelHeader();
                
                    if (modelHeader.modelName === ModelMapping.Mapper.modelName) {
                        this.mapper = new ModelMapping.Mapper();
                        this.mapper.read(reader, modelHeader);
                    }
                    else {
                        throw new Error(`Unexpected model type '${modelHeader.modelName}'; expected '${ModelMapping.Mapper.modelName}'`);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'mapper' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'mapper'`);
                }
        
                return true;
            default:
                return false;
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        // engine
        if (this.isEngineDefined && this.engine !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/abstractModel
            if (this.engine === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
            
                if (this.engine instanceof ConfigitEngine)
                    writer.writeShortValue(0);
                else if (this.engine instanceof MapperEngine)
                    writer.writeShortValue(1);
                else if (this.engine instanceof ReflectionEngine)
                    writer.writeShortValue(2);
                else if (this.engine instanceof FanSelectionEngine)
                    writer.writeShortValue(3);
                else if (this.engine instanceof MacroEngine)
                    writer.writeShortValue(4);
                else if (this.engine instanceof RecommendationEngine)
                    writer.writeShortValue(5);
                else if (this.engine instanceof StaticEngine)
                    writer.writeShortValue(6);
                else if (this.engine instanceof AssignedValueEngine)
                    writer.writeShortValue(7);
                else
                    throw new Error('Unsupported type implementation');
            
                this.engine.slipstreamWrite(writer);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // contracts
        if (this.isContractsDefined && this.contracts !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/array
            if (this.contracts === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                writer.writeIntegerValue(this.contracts.length);
            
                for (const item of this.contracts) {
                    // Item Implementation: model
                    if (item === null) {
                        writer.writeByteValue(0);
                    }
                    else {
                        writer.writeByteValue(1);
                        item.slipstreamWrite(writer);
                    }
                }
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // mapper
        if (this.isMapperDefined && this.mapper !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/model
            if (this.mapper === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                this.mapper.slipstreamWrite(writer);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        // engine
        if (reader.readBooleanValue())
        {
            // Implementation: abstractModel
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null models');
                    break;
                case 1:
                    switch (reader.readShortValue())
                    {
                        case 0:
                            this.engine = new ConfigitEngine();
                            break;
                        case 1:
                            this.engine = new MapperEngine();
                            break;
                        case 2:
                            this.engine = new ReflectionEngine();
                            break;
                        case 3:
                            this.engine = new FanSelectionEngine();
                            break;
                        case 4:
                            this.engine = new MacroEngine();
                            break;
                        case 5:
                            this.engine = new RecommendationEngine();
                            break;
                        case 6:
                            this.engine = new StaticEngine();
                            break;
                        case 7:
                            this.engine = new AssignedValueEngine();
                            break;
                        default:
                            throw new Error('Invalid abstract model code');
                    }
                    this.engine.slipstreamRead(reader);
                    break;
                default:
                    throw new Error('Invalid null code');
            }}
        
        // contracts
        if (reader.readBooleanValue())
        {
            // Implementation: array
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null arrays');
                    break;
                case 1:
                    let item: ModelContract.Contract;
            
                    this.contracts = [];
                    
                    const length = reader.readIntegerValue();
                    for (let i = 0; i < length; i++) {
                        // Item Implementation: model
                        switch (reader.readByteValue())
                        {
                            case 0:
                                throw new Error('Cannot process null models');
                                break;
                            case 1:
                                item = new ModelContract.Contract();
                                item.slipstreamRead(reader);
                                break;
                            default:
                                throw new Error('Invalid null code');
                        }
            
                        this.contracts.push(item);
                    }
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // mapper
        if (reader.readBooleanValue())
        {
            // Implementation: model
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null models');
                    break;
                case 1:
                    this.mapper = new ModelMapping.Mapper();
                    this.mapper.slipstreamRead(reader);
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): ConstraintModel {
        // Method Implementation: simple
        const schema = ConstraintModelZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: ConstraintModelZodSchemaShape): ConstraintModel {
        // Method Implementation: parseZodSchema
        return new ConstraintModel({
            engine: schema.engine && AEngine.parseZodSchema(schema.engine),
            contracts: schema.contracts?.map(i => ModelContract.Contract.parseZodSchema(i)),
            mapper: schema.mapper && ModelMapping.Mapper.parseZodSchema(schema.mapper)
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            engine: this.isEngineDefined ? (this.engine) : undefined,
            contracts: this.isContractsDefined ? (this.contracts?.map(item => item)) : undefined,
            mapper: this.isMapperDefined ? (this.mapper) : undefined,
        };
    }

    public clone(): ConstraintModel {
        // Method Implementation: clone
        return new ConstraintModel({
            engine: this.isEngineDefined ? this.engine.clone() : undefined,
            contracts: this.isContractsDefined ? this.contracts?.map(i => i.clone()) : undefined,
            mapper: this.isMapperDefined ? this.mapper.clone() : undefined
        });
    }
}

export abstract class AEngine implements IEngine {
    public static readonly modelName: string = 'Engine';

    public static readonly propertyNames: ReadonlyArray<string> = [  ];

    public get modelName(): string {
        // Method Implementation: simple
        return AEngine.modelName;
    }

    public constructor();
    public constructor(source: IEngine);
    public constructor(source: {});
    public constructor(source?: IEngine | Partial<AEngine>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            if (isEngineModel(source)) {
            }
            else {
            }
        }
        else {
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return AEngine.propertyNames;
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [];
        
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            default:
                throw new Error(`Property ${propertyName} not recognized`);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            default:
                return false;
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
    }

    public static parse(hash: unknown): AEngine {
        // Method Implementation: simple
        const schema = EngineZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: EngineZodSchemaShape): AEngine {
        // Method Implementation: parseAbstractZodSchema
        switch (schema.engineType)
        {
            case 'configitEngine':
            case 'configit':
                return ConfigitEngine.parse(schema);
            case 'mapperEngine':
            case 'mapper':
                return MapperEngine.parse(schema);
            case 'reflectionEngine':
            case 'reflection':
                return ReflectionEngine.parse(schema);
            case 'fanSelectionEngine':
            case 'fanSelection':
                return FanSelectionEngine.parse(schema);
            case 'macroEngine':
            case 'macro':
                return MacroEngine.parse(schema);
            case 'recommendationEngine':
            case 'recommendation':
                return RecommendationEngine.parse(schema);
            case 'staticEngine':
            case 'static':
                return StaticEngine.parse(schema);
            case 'assignedValueEngine':
            case 'assignedValue':
                return AssignedValueEngine.parse(schema);
            default:
                throw new Error(`engineType of ${ schema.engineType } is not supported`);
        }
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
        };
    }

    public abstract clone(): AEngine;
}

export class ConfigitEngine extends AEngine implements IConfigitEngine {
    public static readonly modelName: string = 'ConfigitEngine';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'vtzUri', 'dependencyMappings' ];

    public get modelName(): string {
        // Method Implementation: simple
        return ConfigitEngine.modelName;
    }

    private _vtzUri: string;

    private _dependencyMappings: ConfigitEngineDependencyMapping[];

    public get vtzUri(): string {
        // Method Implementation: simple
        return this._vtzUri;
    }

    public get dependencyMappings(): ConfigitEngineDependencyMapping[] {
        // Method Implementation: simple
        return this._dependencyMappings;
    }

    public set vtzUri(value: string) {
        // Method Implementation: simple
        this._vtzUri = value;
        this._isVtzUriDefined = true;
    }

    public set dependencyMappings(value: ConfigitEngineDependencyMapping[]) {
        // Method Implementation: simple
        this._dependencyMappings = value;
        this._isDependencyMappingsDefined = true;
    }

    private _isVtzUriDefined: boolean;

    private _isDependencyMappingsDefined: boolean;

    public get isVtzUriDefined(): boolean {
        // Method Implementation: simple
        return this._isVtzUriDefined;
    }

    public get isDependencyMappingsDefined(): boolean {
        // Method Implementation: simple
        return this._isDependencyMappingsDefined;
    }

    public constructor();
    public constructor(source: IConfigitEngine);
    public constructor(source: Partial<Pick<ConfigitEngine, 'vtzUri' | 'dependencyMappings'>>);
    public constructor(source?: IConfigitEngine | Partial<Pick<ConfigitEngine, 'vtzUri' | 'dependencyMappings'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            super(source);
        
            if (isConfigitEngineModel(source)) {
                if (source.isVtzUriDefined) {
                    this._vtzUri = source.vtzUri;
                    this._isVtzUriDefined = true;
                }
                else {
                    this._vtzUri = '';
                    this._isVtzUriDefined = false;
                }
                
                if (source.isDependencyMappingsDefined) {
                    this._dependencyMappings = source.dependencyMappings;
                    this._isDependencyMappingsDefined = true;
                }
                else {
                    this._dependencyMappings = [];
                    this._isDependencyMappingsDefined = true;
                }
            }
            else {
                if (source.vtzUri !== undefined) {
                    this._vtzUri = source.vtzUri;
                    this._isVtzUriDefined = true;
                }
                else {
                    this._vtzUri = '';
                    this._isVtzUriDefined = false;
                }
                
                if (source.dependencyMappings !== undefined) {
                    this._dependencyMappings = source.dependencyMappings;
                    this._isDependencyMappingsDefined = true;
                }
                else {
                    this._dependencyMappings = [];
                    this._isDependencyMappingsDefined = true;
                }
            }
        }
        else {
            super();
        
            this._vtzUri = '';
            this._isVtzUriDefined = false;
        
            this._dependencyMappings = [];
            this._isDependencyMappingsDefined = true;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return [ ...super.getPropertyNames(), ...ConfigitEngine.propertyNames ];
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [ ...super.getDefinedPropertyNames() ];
        
        if (this.isVtzUriDefined && this.vtzUri !== undefined)
            definedProperties.push('vtzUri');
        if (this.isDependencyMappingsDefined && this.dependencyMappings !== undefined)
            definedProperties.push('dependencyMappings');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'vtzUri':
                return this.vtzUri;
            case 'dependencyMappings':
                return this.dependencyMappings;
            default:
                return super.getPropertyValue(propertyName);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        super.write(writer);
        
        if (this.isVtzUriDefined && this.vtzUri !== undefined) {
            if (this.vtzUri !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('vtzUri', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.vtzUri);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('vtzUri', 'null'));
            }
        }
        
        if (this.isDependencyMappingsDefined && this.dependencyMappings !== undefined) {
            if (this.dependencyMappings !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('dependencyMappings', 'array'));
        
                // Implementation: axon/write/array
                writer.writeArrayHeader(new Axon.ArrayHeader(this.dependencyMappings.length));
                for (let i = 0; i < this.dependencyMappings.length; i++) {
                    const item = this.dependencyMappings[i];
                
                    if (item !== null) {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('model'));
                
                        // Implementation: axon/write/model
                        writer.writeModelHeader(new Axon.ModelHeader(item.getModelName(), item.getDefinedPropertyNames().length));
                        item.write(writer);
                    }
                    else {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('null'));
                    }
                }
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('dependencyMappings', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'vtzUri':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.vtzUri = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'vtzUri' cannot be null`);
                    this.vtzUri = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'vtzUri'`);
                }
        
                return true;
            case 'dependencyMappings':
                // Implementation: axon/readProperty/array
                if (propertyHeader.type === 'array') {
                    const arrayHeader = reader.readArrayHeader();
                
                    this.dependencyMappings = [];
                
                    let item: ConfigitEngineDependencyMapping = <any>undefined;
                    for (let i = 0; i < arrayHeader.itemCount; i++) {
                        const arrayItemHeader = reader.readArrayItemHeader();
                
                        // Implementation: model
                        if (arrayItemHeader.type === 'model') {
                            const modelHeader = reader.readModelHeader();
                        
                            if (modelHeader.modelName === ConfigitEngineDependencyMapping.modelName) {
                                item = new ConfigitEngineDependencyMapping();
                                item.read(reader, modelHeader);
                            }
                            else {
                                throw new Error(`Unexpected model type '${modelHeader.modelName}'; expected '${ConfigitEngineDependencyMapping.modelName}'`);
                            }
                        }
                        else if (arrayItemHeader.type === 'null') {
                            throw new Error(`Value for property 'dependencyMappings' cannot be null`);
                        }
                        else {
                            throw new Error(`Unexpected data type '${arrayItemHeader.type}' for property 'dependencyMappings'`);
                        }
                
                        this.dependencyMappings.push(item);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'dependencyMappings' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'dependencyMappings'`);
                }
        
                return true;
            default:
                return super.readProperty(reader, propertyHeader);
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        super.slipstreamWrite(writer);
        
        // vtzUri
        if (this.isVtzUriDefined && this.vtzUri !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.vtzUri === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.vtzUri);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // dependencyMappings
        if (this.isDependencyMappingsDefined && this.dependencyMappings !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/array
            if (this.dependencyMappings === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                writer.writeIntegerValue(this.dependencyMappings.length);
            
                for (const item of this.dependencyMappings) {
                    // Item Implementation: model
                    if (item === null) {
                        writer.writeByteValue(0);
                    }
                    else {
                        writer.writeByteValue(1);
                        item.slipstreamWrite(writer);
                    }
                }
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        super.slipstreamRead(reader);
        
        // vtzUri
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.vtzUri = <any>null;
                    break;
                case 1:
                    this.vtzUri = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // dependencyMappings
        if (reader.readBooleanValue())
        {
            // Implementation: array
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null arrays');
                    break;
                case 1:
                    let item: ConfigitEngineDependencyMapping;
            
                    this.dependencyMappings = [];
                    
                    const length = reader.readIntegerValue();
                    for (let i = 0; i < length; i++) {
                        // Item Implementation: model
                        switch (reader.readByteValue())
                        {
                            case 0:
                                throw new Error('Cannot process null models');
                                break;
                            case 1:
                                item = new ConfigitEngineDependencyMapping();
                                item.slipstreamRead(reader);
                                break;
                            default:
                                throw new Error('Invalid null code');
                        }
            
                        this.dependencyMappings.push(item);
                    }
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): ConfigitEngine {
        // Method Implementation: simple
        const schema = ConfigitEngineZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: ConfigitEngineZodSchemaShape): ConfigitEngine {
        // Method Implementation: parseZodSchema
        return new ConfigitEngine({
            vtzUri: schema.vtzUri,
            dependencyMappings: schema.dependencyMappings?.map(i => ConfigitEngineDependencyMapping.parseZodSchema(i))
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            engineType: 'configit',
            ...super.toJSON(),
            vtzUri: this.isVtzUriDefined ? ((this.vtzUri === null ? undefined : this.vtzUri)) : undefined,
            dependencyMappings: this.isDependencyMappingsDefined ? (this.dependencyMappings?.map(item => item)) : undefined,
        };
    }

    public clone(): ConfigitEngine {
        // Method Implementation: clone
        return new ConfigitEngine({
            vtzUri: this.isVtzUriDefined ? this.vtzUri : undefined,
            dependencyMappings: this.isDependencyMappingsDefined ? this.dependencyMappings?.map(i => i.clone()) : undefined
        });
    }
}

export class ConfigitEngineDependencyMapping implements IConfigitEngineDependencyMapping {
    public static readonly modelName: string = 'ConfigitEngineDependencyMapping';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'variable', 'dependentVariables' ];

    public get modelName(): string {
        // Method Implementation: simple
        return ConfigitEngineDependencyMapping.modelName;
    }

    private _variable: string;

    private _dependentVariables: string[];

    public get variable(): string {
        // Method Implementation: simple
        return this._variable;
    }

    public get dependentVariables(): string[] {
        // Method Implementation: simple
        return this._dependentVariables;
    }

    public set variable(value: string) {
        // Method Implementation: simple
        this._variable = value;
        this._isVariableDefined = true;
    }

    public set dependentVariables(value: string[]) {
        // Method Implementation: simple
        this._dependentVariables = value;
        this._isDependentVariablesDefined = true;
    }

    private _isVariableDefined: boolean;

    private _isDependentVariablesDefined: boolean;

    public get isVariableDefined(): boolean {
        // Method Implementation: simple
        return this._isVariableDefined;
    }

    public get isDependentVariablesDefined(): boolean {
        // Method Implementation: simple
        return this._isDependentVariablesDefined;
    }

    public constructor();
    public constructor(source: IConfigitEngineDependencyMapping);
    public constructor(source: Partial<Pick<ConfigitEngineDependencyMapping, 'variable' | 'dependentVariables'>>);
    public constructor(source?: IConfigitEngineDependencyMapping | Partial<Pick<ConfigitEngineDependencyMapping, 'variable' | 'dependentVariables'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            if (isConfigitEngineDependencyMappingModel(source)) {
                if (source.isVariableDefined) {
                    this._variable = source.variable;
                    this._isVariableDefined = true;
                }
                else {
                    this._variable = '';
                    this._isVariableDefined = false;
                }
                
                if (source.isDependentVariablesDefined) {
                    this._dependentVariables = source.dependentVariables;
                    this._isDependentVariablesDefined = true;
                }
                else {
                    this._dependentVariables = [];
                    this._isDependentVariablesDefined = true;
                }
            }
            else {
                if (source.variable !== undefined) {
                    this._variable = source.variable;
                    this._isVariableDefined = true;
                }
                else {
                    this._variable = '';
                    this._isVariableDefined = false;
                }
                
                if (source.dependentVariables !== undefined) {
                    this._dependentVariables = source.dependentVariables;
                    this._isDependentVariablesDefined = true;
                }
                else {
                    this._dependentVariables = [];
                    this._isDependentVariablesDefined = true;
                }
            }
        }
        else {
            this._variable = '';
            this._isVariableDefined = false;
        
            this._dependentVariables = [];
            this._isDependentVariablesDefined = true;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return ConfigitEngineDependencyMapping.propertyNames;
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [];
        
        if (this.isVariableDefined && this.variable !== undefined)
            definedProperties.push('variable');
        if (this.isDependentVariablesDefined && this.dependentVariables !== undefined)
            definedProperties.push('dependentVariables');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'variable':
                return this.variable;
            case 'dependentVariables':
                return this.dependentVariables;
            default:
                throw new Error(`Property ${propertyName} not recognized`);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        if (this.isVariableDefined && this.variable !== undefined) {
            if (this.variable !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('variable', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.variable);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('variable', 'null'));
            }
        }
        
        if (this.isDependentVariablesDefined && this.dependentVariables !== undefined) {
            if (this.dependentVariables !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('dependentVariables', 'array'));
        
                // Implementation: axon/write/array
                writer.writeArrayHeader(new Axon.ArrayHeader(this.dependentVariables.length));
                for (let i = 0; i < this.dependentVariables.length; i++) {
                    const item = this.dependentVariables[i];
                
                    if (item !== null) {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('string'));
                
                        // Implementation: axon/write/string
                        writer.writeStringValue(item);
                    }
                    else {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('null'));
                    }
                }
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('dependentVariables', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'variable':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.variable = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'variable' cannot be null`);
                    this.variable = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'variable'`);
                }
        
                return true;
            case 'dependentVariables':
                // Implementation: axon/readProperty/array
                if (propertyHeader.type === 'array') {
                    const arrayHeader = reader.readArrayHeader();
                
                    this.dependentVariables = [];
                
                    let item: string = <any>undefined;
                    for (let i = 0; i < arrayHeader.itemCount; i++) {
                        const arrayItemHeader = reader.readArrayItemHeader();
                
                        // Implementation: string
                        if (arrayItemHeader.type === 'string') {
                            item = reader.readStringValue();
                        }
                        else if (arrayItemHeader.type === 'null') {
                            // Do Nothing - TODO Review
                            // throw new Error(`Value for property 'dependentVariables' cannot be null`);
                            item = <any>null;
                        }
                        else {
                            throw new Error(`Unexpected data type '${arrayItemHeader.type}' for property 'dependentVariables'`);
                        }
                
                        this.dependentVariables.push(item);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'dependentVariables' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'dependentVariables'`);
                }
        
                return true;
            default:
                return false;
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        // variable
        if (this.isVariableDefined && this.variable !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.variable === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.variable);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // dependentVariables
        if (this.isDependentVariablesDefined && this.dependentVariables !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/array
            if (this.dependentVariables === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                writer.writeIntegerValue(this.dependentVariables.length);
            
                for (const item of this.dependentVariables) {
                    // Item Implementation: string
                    if (item === null)
                    {
                        writer.writeByteValue(0);
                    }
                    else
                    {
                        writer.writeByteValue(1);
                        writer.writeStringValue(item);
                    }
                }
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        // variable
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.variable = <any>null;
                    break;
                case 1:
                    this.variable = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // dependentVariables
        if (reader.readBooleanValue())
        {
            // Implementation: array
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null arrays');
                    break;
                case 1:
                    let item: string;
            
                    this.dependentVariables = [];
                    
                    const length = reader.readIntegerValue();
                    for (let i = 0; i < length; i++) {
                        // Item Implementation: string
                        switch (reader.readByteValue())
                        {
                            case 0:
                                item = <any>null;
                                break;
                            case 1:
                                item = reader.readStringValue();
                                break;
                            default:
                                throw new Error('Invalid null code');
                        }
            
                        this.dependentVariables.push(item);
                    }
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): ConfigitEngineDependencyMapping {
        // Method Implementation: simple
        const schema = ConfigitEngineDependencyMappingZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: ConfigitEngineDependencyMappingZodSchemaShape): ConfigitEngineDependencyMapping {
        // Method Implementation: parseZodSchema
        return new ConfigitEngineDependencyMapping({
            variable: schema.variable,
            dependentVariables: schema.dependentVariables?.map(i => i)
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            variable: this.isVariableDefined ? ((this.variable === null ? undefined : this.variable)) : undefined,
            dependentVariables: this.isDependentVariablesDefined ? (this.dependentVariables?.map(item => (item === null ? undefined : item))) : undefined,
        };
    }

    public clone(): ConfigitEngineDependencyMapping {
        // Method Implementation: clone
        return new ConfigitEngineDependencyMapping({
            variable: this.isVariableDefined ? this.variable : undefined,
            dependentVariables: this.isDependentVariablesDefined ? this.dependentVariables?.map(i => i) : undefined
        });
    }
}

export class MapperEngine extends AEngine implements IMapperEngine {
    public static readonly modelName: string = 'MapperEngine';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'parentContract', 'decouple', 'decoupled', 'decoupledValues' ];

    public get modelName(): string {
        // Method Implementation: simple
        return MapperEngine.modelName;
    }

    private _parentContract: ModelContract.Contract;

    private _decouple: string;

    private _decoupled: boolean;

    private _decoupledValues: Record<string, string>;

    public get parentContract(): ModelContract.Contract {
        // Method Implementation: simple
        return this._parentContract;
    }

    public get decouple(): string {
        // Method Implementation: simple
        return this._decouple;
    }

    public get decoupled(): boolean {
        // Method Implementation: simple
        return this._decoupled;
    }

    public get decoupledValues(): Record<string, string> {
        // Method Implementation: simple
        return this._decoupledValues;
    }

    public set parentContract(value: ModelContract.Contract) {
        // Method Implementation: simple
        this._parentContract = value;
        this._isParentContractDefined = true;
    }

    public set decouple(value: string) {
        // Method Implementation: simple
        this._decouple = value;
        this._isDecoupleDefined = true;
    }

    public set decoupled(value: boolean) {
        // Method Implementation: simple
        this._decoupled = value;
        this._isDecoupledDefined = true;
    }

    public set decoupledValues(value: Record<string, string>) {
        // Method Implementation: simple
        this._decoupledValues = value;
        this._isDecoupledValuesDefined = true;
    }

    private _isParentContractDefined: boolean;

    private _isDecoupleDefined: boolean;

    private _isDecoupledDefined: boolean;

    private _isDecoupledValuesDefined: boolean;

    public get isParentContractDefined(): boolean {
        // Method Implementation: simple
        return this._isParentContractDefined;
    }

    public get isDecoupleDefined(): boolean {
        // Method Implementation: simple
        return this._isDecoupleDefined;
    }

    public get isDecoupledDefined(): boolean {
        // Method Implementation: simple
        return this._isDecoupledDefined;
    }

    public get isDecoupledValuesDefined(): boolean {
        // Method Implementation: simple
        return this._isDecoupledValuesDefined;
    }

    public constructor();
    public constructor(source: IMapperEngine);
    public constructor(source: Partial<Pick<MapperEngine, 'parentContract' | 'decouple' | 'decoupled' | 'decoupledValues'>>);
    public constructor(source?: IMapperEngine | Partial<Pick<MapperEngine, 'parentContract' | 'decouple' | 'decoupled' | 'decoupledValues'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            super(source);
        
            if (isMapperEngineModel(source)) {
                if (source.isParentContractDefined) {
                    this._parentContract = source.parentContract;
                    this._isParentContractDefined = true;
                }
                else {
                    this._parentContract = new ModelContract.Contract();
                    this._isParentContractDefined = true;
                }
                
                if (source.isDecoupleDefined) {
                    this._decouple = source.decouple;
                    this._isDecoupleDefined = true;
                }
                else {
                    this._decouple = '';
                    this._isDecoupleDefined = false;
                }
                
                if (source.isDecoupledDefined) {
                    this._decoupled = source.decoupled;
                    this._isDecoupledDefined = true;
                }
                else {
                    this._decoupled = false;
                    this._isDecoupledDefined = false;
                }
                
                if (source.isDecoupledValuesDefined) {
                    this._decoupledValues = source.decoupledValues;
                    this._isDecoupledValuesDefined = true;
                }
                else {
                    this._decoupledValues = {};
                    this._isDecoupledValuesDefined = true;
                }
            }
            else {
                if (source.parentContract !== undefined) {
                    this._parentContract = source.parentContract;
                    this._isParentContractDefined = true;
                }
                else {
                    this._parentContract = new ModelContract.Contract();
                    this._isParentContractDefined = true;
                }
                
                if (source.decouple !== undefined) {
                    this._decouple = source.decouple;
                    this._isDecoupleDefined = true;
                }
                else {
                    this._decouple = '';
                    this._isDecoupleDefined = false;
                }
                
                if (source.decoupled !== undefined) {
                    this._decoupled = source.decoupled;
                    this._isDecoupledDefined = true;
                }
                else {
                    this._decoupled = false;
                    this._isDecoupledDefined = false;
                }
                
                if (source.decoupledValues !== undefined) {
                    this._decoupledValues = source.decoupledValues;
                    this._isDecoupledValuesDefined = true;
                }
                else {
                    this._decoupledValues = {};
                    this._isDecoupledValuesDefined = true;
                }
            }
        }
        else {
            super();
        
            this._parentContract = new ModelContract.Contract();
            this._isParentContractDefined = true;
        
            this._decouple = '';
            this._isDecoupleDefined = false;
        
            this._decoupled = false;
            this._isDecoupledDefined = false;
        
            this._decoupledValues = {};
            this._isDecoupledValuesDefined = true;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return [ ...super.getPropertyNames(), ...MapperEngine.propertyNames ];
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [ ...super.getDefinedPropertyNames() ];
        
        if (this.isParentContractDefined && this.parentContract !== undefined)
            definedProperties.push('parentContract');
        if (this.isDecoupleDefined && this.decouple !== undefined)
            definedProperties.push('decouple');
        if (this.isDecoupledDefined && this.decoupled !== undefined)
            definedProperties.push('decoupled');
        if (this.isDecoupledValuesDefined && this.decoupledValues !== undefined)
            definedProperties.push('decoupledValues');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'parentContract':
                return this.parentContract;
            case 'decouple':
                return this.decouple;
            case 'decoupled':
                return this.decoupled;
            case 'decoupledValues':
                return this.decoupledValues;
            default:
                return super.getPropertyValue(propertyName);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        super.write(writer);
        
        if (this.isParentContractDefined && this.parentContract !== undefined) {
            if (this.parentContract !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('parentContract', 'model'));
        
                // Implementation: axon/write/model
                writer.writeModelHeader(new Axon.ModelHeader(this.parentContract.getModelName(), this.parentContract.getDefinedPropertyNames().length));
                this.parentContract.write(writer);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('parentContract', 'null'));
            }
        }
        
        if (this.isDecoupleDefined && this.decouple !== undefined) {
            if (this.decouple !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('decouple', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.decouple);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('decouple', 'null'));
            }
        }
        
        if (this.isDecoupledDefined && this.decoupled !== undefined) {
            if (this.decoupled !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('decoupled', 'boolean'));
        
                // Implementation: axon/write/boolean
                writer.writeBooleanValue(this.decoupled);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('decoupled', 'null'));
            }
        }
        
        if (this.isDecoupledValuesDefined && this.decoupledValues !== undefined) {
            if (this.decoupledValues !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('decoupledValues', 'dictionary'));
        
                // Implementation: axon/write/dictionary
                writer.writeDictionaryHeader(new Axon.DictionaryHeader(_.keys(this.decoupledValues).length));
                for (let key in this.decoupledValues) {
                    const dictionaryValue = this.decoupledValues[key];
                
                    writer.writeDictionaryItemHeader(new Axon.DictionaryItemHeader('string', dictionaryValue !== null ? 'string' : 'null'));
                
                    // Key Implementation: axon/write/string
                    writer.writeStringValue(key);
                
                    if (dictionaryValue !== null) {
                        // Implementation: axon/write/string
                        writer.writeStringValue(dictionaryValue);
                    }
                }
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('decoupledValues', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'parentContract':
                // Implementation: axon/readProperty/model
                if (propertyHeader.type === 'model') {
                    const modelHeader = reader.readModelHeader();
                
                    if (modelHeader.modelName === ModelContract.Contract.modelName) {
                        this.parentContract = new ModelContract.Contract();
                        this.parentContract.read(reader, modelHeader);
                    }
                    else {
                        throw new Error(`Unexpected model type '${modelHeader.modelName}'; expected '${ModelContract.Contract.modelName}'`);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'parentContract' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'parentContract'`);
                }
        
                return true;
            case 'decouple':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.decouple = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'decouple' cannot be null`);
                    this.decouple = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'decouple'`);
                }
        
                return true;
            case 'decoupled':
                // Implementation: axon/readProperty/boolean
                if (propertyHeader.type === 'boolean') {
                    this.decoupled = reader.readBooleanValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'decoupled' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'decoupled'`);
                }
        
                return true;
            case 'decoupledValues':
                // Implementation: axon/readProperty/dictionary
                if (propertyHeader.type === 'dictionary') {
                    const dictionaryHeader = reader.readDictionaryHeader();
                
                    this.decoupledValues = {};
                
                    for (let i = 0; i < dictionaryHeader.recordCount; i++) {
                        let key: string = <any>undefined;
                        let value: string = <any>undefined;
                
                        const dictionaryItemHeader = reader.readDictionaryItemHeader();
                
                        // Key Implementation: string
                        if (dictionaryItemHeader.keyType === 'string') {
                            key = reader.readStringValue();
                        }
                        else if (dictionaryItemHeader.keyType === 'null') {
                            // Do Nothing - TODO Review
                            // throw new Error(`Value for property 'decoupledValues' cannot be null`);
                            key = <any>null;
                        }
                        else {
                            throw new Error(`Unexpected data type '${dictionaryItemHeader.keyType}' for property 'decoupledValues'`);
                        }
                
                        // Value Implementation: string
                        if (dictionaryItemHeader.valueType === 'string') {
                            value = reader.readStringValue();
                        }
                        else if (dictionaryItemHeader.valueType === 'null') {
                            // Do Nothing - TODO Review
                            // throw new Error(`Value for property 'decoupledValues' cannot be null`);
                            value = <any>null;
                        }
                        else {
                            throw new Error(`Unexpected data type '${dictionaryItemHeader.valueType}' for property 'decoupledValues'`);
                        }
                
                        this.decoupledValues[key] = value;
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'decoupledValues' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'decoupledValues'`);
                }
        
                return true;
            default:
                return super.readProperty(reader, propertyHeader);
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        super.slipstreamWrite(writer);
        
        // parentContract
        if (this.isParentContractDefined && this.parentContract !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/model
            if (this.parentContract === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                this.parentContract.slipstreamWrite(writer);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // decouple
        if (this.isDecoupleDefined && this.decouple !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.decouple === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.decouple);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // decoupled
        if (this.isDecoupledDefined && this.decoupled !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/boolean
            writer.writeBooleanValue(this.decoupled);
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // decoupledValues
        if (this.isDecoupledValuesDefined && this.decoupledValues !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/dictionary
            if (this.decoupledValues === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                writer.writeIntegerValue(_.keys(this.decoupledValues).length);
            
                for (let key in this.decoupledValues) {
                    // Key Implementation: string
                    if (key === null)
                    {
                        writer.writeByteValue(0);
                    }
                    else
                    {
                        writer.writeByteValue(1);
                        writer.writeStringValue(key);
                    }
            
                    // Value Implementation: string
                    if (this.decoupledValues[key] === null)
                    {
                        writer.writeByteValue(0);
                    }
                    else
                    {
                        writer.writeByteValue(1);
                        writer.writeStringValue(this.decoupledValues[key]);
                    }
                }
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        super.slipstreamRead(reader);
        
        // parentContract
        if (reader.readBooleanValue())
        {
            // Implementation: model
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null models');
                    break;
                case 1:
                    this.parentContract = new ModelContract.Contract();
                    this.parentContract.slipstreamRead(reader);
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // decouple
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.decouple = <any>null;
                    break;
                case 1:
                    this.decouple = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // decoupled
        if (reader.readBooleanValue())
        {
            // Implementation: boolean
            this.decoupled = reader.readBooleanValue();
        }
        
        // decoupledValues
        if (reader.readBooleanValue())
        {
            // Implementation: dictionary
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null dictionaries');
                    break;
                case 1:
                    let key: string;
                    let value: string;
            
                    this.decoupledValues = {};
                    
                    const length = reader.readIntegerValue();
                    for (let i = 0; i < length; i++) {
                        // Key Implementation: string
                        switch (reader.readByteValue())
                        {
                            case 0:
                                key = <any>null;
                                break;
                            case 1:
                                key = reader.readStringValue();
                                break;
                            default:
                                throw new Error('Invalid null code');
                        }
            
                        // Value Implementation: string
                        switch (reader.readByteValue())
                        {
                            case 0:
                                value = <any>null;
                                break;
                            case 1:
                                value = reader.readStringValue();
                                break;
                            default:
                                throw new Error('Invalid null code');
                        }
            
                        this.decoupledValues[key] = value;
                    }
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): MapperEngine {
        // Method Implementation: simple
        const schema = MapperEngineZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: MapperEngineZodSchemaShape): MapperEngine {
        // Method Implementation: parseZodSchema
        return new MapperEngine({
            parentContract: schema.parentContract && ModelContract.Contract.parseZodSchema(schema.parentContract),
            decouple: schema.decouple,
            decoupled: schema.decoupled,
            decoupledValues: schema.decoupledValues
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            engineType: 'mapper',
            ...super.toJSON(),
            parentContract: this.isParentContractDefined ? (this.parentContract) : undefined,
            decouple: this.isDecoupleDefined ? ((this.decouple === null ? undefined : this.decouple)) : undefined,
            decoupled: this.isDecoupledDefined ? (this.decoupled) : undefined,
            decoupledValues: this.isDecoupledValuesDefined ? (this.decoupledValues) : undefined,
        };
    }

    public clone(): MapperEngine {
        // Method Implementation: clone
        return new MapperEngine({
            parentContract: this.isParentContractDefined ? this.parentContract.clone() : undefined,
            decouple: this.isDecoupleDefined ? this.decouple : undefined,
            decoupled: this.isDecoupledDefined ? this.decoupled : undefined,
            decoupledValues: this.isDecoupledValuesDefined ? this.decoupledValues : undefined
        });
    }
}

export class ReflectionEngine extends AEngine implements IReflectionEngine {
    public static readonly modelName: string = 'ReflectionEngine';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'type' ];

    public get modelName(): string {
        // Method Implementation: simple
        return ReflectionEngine.modelName;
    }

    private _type: string;

    public get type(): string {
        // Method Implementation: simple
        return this._type;
    }

    public set type(value: string) {
        // Method Implementation: simple
        this._type = value;
        this._isTypeDefined = true;
    }

    private _isTypeDefined: boolean;

    public get isTypeDefined(): boolean {
        // Method Implementation: simple
        return this._isTypeDefined;
    }

    public constructor();
    public constructor(source: IReflectionEngine);
    public constructor(source: Partial<Pick<ReflectionEngine, 'type'>>);
    public constructor(source?: IReflectionEngine | Partial<Pick<ReflectionEngine, 'type'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            super(source);
        
            if (isReflectionEngineModel(source)) {
                if (source.isTypeDefined) {
                    this._type = source.type;
                    this._isTypeDefined = true;
                }
                else {
                    this._type = '';
                    this._isTypeDefined = false;
                }
            }
            else {
                if (source.type !== undefined) {
                    this._type = source.type;
                    this._isTypeDefined = true;
                }
                else {
                    this._type = '';
                    this._isTypeDefined = false;
                }
            }
        }
        else {
            super();
        
            this._type = '';
            this._isTypeDefined = false;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return [ ...super.getPropertyNames(), ...ReflectionEngine.propertyNames ];
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [ ...super.getDefinedPropertyNames() ];
        
        if (this.isTypeDefined && this.type !== undefined)
            definedProperties.push('type');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'type':
                return this.type;
            default:
                return super.getPropertyValue(propertyName);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        super.write(writer);
        
        if (this.isTypeDefined && this.type !== undefined) {
            if (this.type !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('type', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.type);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('type', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'type':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.type = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'type' cannot be null`);
                    this.type = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'type'`);
                }
        
                return true;
            default:
                return super.readProperty(reader, propertyHeader);
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        super.slipstreamWrite(writer);
        
        // type
        if (this.isTypeDefined && this.type !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.type === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.type);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        super.slipstreamRead(reader);
        
        // type
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.type = <any>null;
                    break;
                case 1:
                    this.type = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): ReflectionEngine {
        // Method Implementation: simple
        const schema = ReflectionEngineZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: ReflectionEngineZodSchemaShape): ReflectionEngine {
        // Method Implementation: parseZodSchema
        return new ReflectionEngine({
            type: schema.type
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            engineType: 'reflection',
            ...super.toJSON(),
            type: this.isTypeDefined ? ((this.type === null ? undefined : this.type)) : undefined,
        };
    }

    public clone(): ReflectionEngine {
        // Method Implementation: clone
        return new ReflectionEngine({
            type: this.isTypeDefined ? this.type : undefined
        });
    }
}

export class FanSelectionEngine extends AEngine implements IFanSelectionEngine {
    public static readonly modelName: string = 'FanSelectionEngine';

    public static readonly propertyNames: ReadonlyArray<string> = [  ];

    public get modelName(): string {
        // Method Implementation: simple
        return FanSelectionEngine.modelName;
    }

    public constructor();
    public constructor(source: IFanSelectionEngine);
    public constructor(source: {});
    public constructor(source?: IFanSelectionEngine | Partial<FanSelectionEngine>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            super(source);
        
            if (isFanSelectionEngineModel(source)) {
            }
            else {
            }
        }
        else {
            super();
        
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return [ ...super.getPropertyNames(), ...FanSelectionEngine.propertyNames ];
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [ ...super.getDefinedPropertyNames() ];
        
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            default:
                return super.getPropertyValue(propertyName);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        super.write(writer);
        
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            default:
                return super.readProperty(reader, propertyHeader);
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        super.slipstreamWrite(writer);
        
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        super.slipstreamRead(reader);
        
    }

    public static parse(hash: unknown): FanSelectionEngine {
        // Method Implementation: simple
        const schema = FanSelectionEngineZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: FanSelectionEngineZodSchemaShape): FanSelectionEngine {
        // Method Implementation: parseZodSchema
        return new FanSelectionEngine({
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            engineType: 'fanSelection',
            ...super.toJSON(),
        };
    }

    public clone(): FanSelectionEngine {
        // Method Implementation: clone
        return new FanSelectionEngine({
        });
    }
}

export class MacroEngine extends AEngine implements IMacroEngine {
    public static readonly modelName: string = 'MacroEngine';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'values' ];

    public get modelName(): string {
        // Method Implementation: simple
        return MacroEngine.modelName;
    }

    private _values: MacroEngineValue[];

    public get values(): MacroEngineValue[] {
        // Method Implementation: simple
        return this._values;
    }

    public set values(value: MacroEngineValue[]) {
        // Method Implementation: simple
        this._values = value;
        this._isValuesDefined = true;
    }

    private _isValuesDefined: boolean;

    public get isValuesDefined(): boolean {
        // Method Implementation: simple
        return this._isValuesDefined;
    }

    public constructor();
    public constructor(source: IMacroEngine);
    public constructor(source: Partial<Pick<MacroEngine, 'values'>>);
    public constructor(source?: IMacroEngine | Partial<Pick<MacroEngine, 'values'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            super(source);
        
            if (isMacroEngineModel(source)) {
                if (source.isValuesDefined) {
                    this._values = source.values;
                    this._isValuesDefined = true;
                }
                else {
                    this._values = [];
                    this._isValuesDefined = true;
                }
            }
            else {
                if (source.values !== undefined) {
                    this._values = source.values;
                    this._isValuesDefined = true;
                }
                else {
                    this._values = [];
                    this._isValuesDefined = true;
                }
            }
        }
        else {
            super();
        
            this._values = [];
            this._isValuesDefined = true;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return [ ...super.getPropertyNames(), ...MacroEngine.propertyNames ];
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [ ...super.getDefinedPropertyNames() ];
        
        if (this.isValuesDefined && this.values !== undefined)
            definedProperties.push('values');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'values':
                return this.values;
            default:
                return super.getPropertyValue(propertyName);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        super.write(writer);
        
        if (this.isValuesDefined && this.values !== undefined) {
            if (this.values !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('values', 'array'));
        
                // Implementation: axon/write/array
                writer.writeArrayHeader(new Axon.ArrayHeader(this.values.length));
                for (let i = 0; i < this.values.length; i++) {
                    const item = this.values[i];
                
                    if (item !== null) {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('model'));
                
                        // Implementation: axon/write/model
                        writer.writeModelHeader(new Axon.ModelHeader(item.getModelName(), item.getDefinedPropertyNames().length));
                        item.write(writer);
                    }
                    else {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('null'));
                    }
                }
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('values', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'values':
                // Implementation: axon/readProperty/array
                if (propertyHeader.type === 'array') {
                    const arrayHeader = reader.readArrayHeader();
                
                    this.values = [];
                
                    let item: MacroEngineValue = <any>undefined;
                    for (let i = 0; i < arrayHeader.itemCount; i++) {
                        const arrayItemHeader = reader.readArrayItemHeader();
                
                        // Implementation: model
                        if (arrayItemHeader.type === 'model') {
                            const modelHeader = reader.readModelHeader();
                        
                            if (modelHeader.modelName === MacroEngineValue.modelName) {
                                item = new MacroEngineValue();
                                item.read(reader, modelHeader);
                            }
                            else {
                                throw new Error(`Unexpected model type '${modelHeader.modelName}'; expected '${MacroEngineValue.modelName}'`);
                            }
                        }
                        else if (arrayItemHeader.type === 'null') {
                            throw new Error(`Value for property 'values' cannot be null`);
                        }
                        else {
                            throw new Error(`Unexpected data type '${arrayItemHeader.type}' for property 'values'`);
                        }
                
                        this.values.push(item);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'values' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'values'`);
                }
        
                return true;
            default:
                return super.readProperty(reader, propertyHeader);
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        super.slipstreamWrite(writer);
        
        // values
        if (this.isValuesDefined && this.values !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/array
            if (this.values === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                writer.writeIntegerValue(this.values.length);
            
                for (const item of this.values) {
                    // Item Implementation: model
                    if (item === null) {
                        writer.writeByteValue(0);
                    }
                    else {
                        writer.writeByteValue(1);
                        item.slipstreamWrite(writer);
                    }
                }
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        super.slipstreamRead(reader);
        
        // values
        if (reader.readBooleanValue())
        {
            // Implementation: array
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null arrays');
                    break;
                case 1:
                    let item: MacroEngineValue;
            
                    this.values = [];
                    
                    const length = reader.readIntegerValue();
                    for (let i = 0; i < length; i++) {
                        // Item Implementation: model
                        switch (reader.readByteValue())
                        {
                            case 0:
                                throw new Error('Cannot process null models');
                                break;
                            case 1:
                                item = new MacroEngineValue();
                                item.slipstreamRead(reader);
                                break;
                            default:
                                throw new Error('Invalid null code');
                        }
            
                        this.values.push(item);
                    }
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): MacroEngine {
        // Method Implementation: simple
        const schema = MacroEngineZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: MacroEngineZodSchemaShape): MacroEngine {
        // Method Implementation: parseZodSchema
        return new MacroEngine({
            values: schema.values?.map(i => MacroEngineValue.parseZodSchema(i))
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            engineType: 'macro',
            ...super.toJSON(),
            values: this.isValuesDefined ? (this.values?.map(item => item)) : undefined,
        };
    }

    public clone(): MacroEngine {
        // Method Implementation: clone
        return new MacroEngine({
            values: this.isValuesDefined ? this.values?.map(i => i.clone()) : undefined
        });
    }
}

export class MacroEngineValue implements IMacroEngineValue {
    public static readonly modelName: string = 'MacroEngineValue';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'name', 'value' ];

    public get modelName(): string {
        // Method Implementation: simple
        return MacroEngineValue.modelName;
    }

    private _name: string;

    private _value: string;

    public get name(): string {
        // Method Implementation: simple
        return this._name;
    }

    public get value(): string {
        // Method Implementation: simple
        return this._value;
    }

    public set name(value: string) {
        // Method Implementation: simple
        this._name = value;
        this._isNameDefined = true;
    }

    public set value(value: string) {
        // Method Implementation: simple
        this._value = value;
        this._isValueDefined = true;
    }

    private _isNameDefined: boolean;

    private _isValueDefined: boolean;

    public get isNameDefined(): boolean {
        // Method Implementation: simple
        return this._isNameDefined;
    }

    public get isValueDefined(): boolean {
        // Method Implementation: simple
        return this._isValueDefined;
    }

    public constructor();
    public constructor(source: IMacroEngineValue);
    public constructor(source: Partial<Pick<MacroEngineValue, 'name' | 'value'>>);
    public constructor(source?: IMacroEngineValue | Partial<Pick<MacroEngineValue, 'name' | 'value'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            if (isMacroEngineValueModel(source)) {
                if (source.isNameDefined) {
                    this._name = source.name;
                    this._isNameDefined = true;
                }
                else {
                    this._name = '';
                    this._isNameDefined = false;
                }
                
                if (source.isValueDefined) {
                    this._value = source.value;
                    this._isValueDefined = true;
                }
                else {
                    this._value = '';
                    this._isValueDefined = false;
                }
            }
            else {
                if (source.name !== undefined) {
                    this._name = source.name;
                    this._isNameDefined = true;
                }
                else {
                    this._name = '';
                    this._isNameDefined = false;
                }
                
                if (source.value !== undefined) {
                    this._value = source.value;
                    this._isValueDefined = true;
                }
                else {
                    this._value = '';
                    this._isValueDefined = false;
                }
            }
        }
        else {
            this._name = '';
            this._isNameDefined = false;
        
            this._value = '';
            this._isValueDefined = false;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return MacroEngineValue.propertyNames;
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [];
        
        if (this.isNameDefined && this.name !== undefined)
            definedProperties.push('name');
        if (this.isValueDefined && this.value !== undefined)
            definedProperties.push('value');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'name':
                return this.name;
            case 'value':
                return this.value;
            default:
                throw new Error(`Property ${propertyName} not recognized`);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        if (this.isNameDefined && this.name !== undefined) {
            if (this.name !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('name', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.name);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('name', 'null'));
            }
        }
        
        if (this.isValueDefined && this.value !== undefined) {
            if (this.value !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('value', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.value);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('value', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'name':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.name = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'name' cannot be null`);
                    this.name = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'name'`);
                }
        
                return true;
            case 'value':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.value = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'value' cannot be null`);
                    this.value = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'value'`);
                }
        
                return true;
            default:
                return false;
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        // name
        if (this.isNameDefined && this.name !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.name === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.name);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // value
        if (this.isValueDefined && this.value !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.value === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.value);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        // name
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.name = <any>null;
                    break;
                case 1:
                    this.name = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // value
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.value = <any>null;
                    break;
                case 1:
                    this.value = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): MacroEngineValue {
        // Method Implementation: simple
        const schema = MacroEngineValueZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: MacroEngineValueZodSchemaShape): MacroEngineValue {
        // Method Implementation: parseZodSchema
        return new MacroEngineValue({
            name: schema.name,
            value: schema.value
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            name: this.isNameDefined ? ((this.name === null ? undefined : this.name)) : undefined,
            value: this.isValueDefined ? ((this.value === null ? undefined : this.value)) : undefined,
        };
    }

    public clone(): MacroEngineValue {
        // Method Implementation: clone
        return new MacroEngineValue({
            name: this.isNameDefined ? this.name : undefined,
            value: this.isValueDefined ? this.value : undefined
        });
    }
}

export class RecommendationEngine extends AEngine implements IRecommendationEngine {
    public static readonly modelName: string = 'RecommendationEngine';

    public static readonly propertyNames: ReadonlyArray<string> = [  ];

    public get modelName(): string {
        // Method Implementation: simple
        return RecommendationEngine.modelName;
    }

    public constructor();
    public constructor(source: IRecommendationEngine);
    public constructor(source: {});
    public constructor(source?: IRecommendationEngine | Partial<RecommendationEngine>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            super(source);
        
            if (isRecommendationEngineModel(source)) {
            }
            else {
            }
        }
        else {
            super();
        
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return [ ...super.getPropertyNames(), ...RecommendationEngine.propertyNames ];
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [ ...super.getDefinedPropertyNames() ];
        
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            default:
                return super.getPropertyValue(propertyName);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        super.write(writer);
        
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            default:
                return super.readProperty(reader, propertyHeader);
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        super.slipstreamWrite(writer);
        
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        super.slipstreamRead(reader);
        
    }

    public static parse(hash: unknown): RecommendationEngine {
        // Method Implementation: simple
        const schema = RecommendationEngineZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: RecommendationEngineZodSchemaShape): RecommendationEngine {
        // Method Implementation: parseZodSchema
        return new RecommendationEngine({
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            engineType: 'recommendation',
            ...super.toJSON(),
        };
    }

    public clone(): RecommendationEngine {
        // Method Implementation: clone
        return new RecommendationEngine({
        });
    }
}

export class StaticEngine extends AEngine implements IStaticEngine {
    public static readonly modelName: string = 'StaticEngine';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'values' ];

    public get modelName(): string {
        // Method Implementation: simple
        return StaticEngine.modelName;
    }

    private _values: Record<string, string>;

    public get values(): Record<string, string> {
        // Method Implementation: simple
        return this._values;
    }

    public set values(value: Record<string, string>) {
        // Method Implementation: simple
        this._values = value;
        this._isValuesDefined = true;
    }

    private _isValuesDefined: boolean;

    public get isValuesDefined(): boolean {
        // Method Implementation: simple
        return this._isValuesDefined;
    }

    public constructor();
    public constructor(source: IStaticEngine);
    public constructor(source: Partial<Pick<StaticEngine, 'values'>>);
    public constructor(source?: IStaticEngine | Partial<Pick<StaticEngine, 'values'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            super(source);
        
            if (isStaticEngineModel(source)) {
                if (source.isValuesDefined) {
                    this._values = source.values;
                    this._isValuesDefined = true;
                }
                else {
                    this._values = {};
                    this._isValuesDefined = true;
                }
            }
            else {
                if (source.values !== undefined) {
                    this._values = source.values;
                    this._isValuesDefined = true;
                }
                else {
                    this._values = {};
                    this._isValuesDefined = true;
                }
            }
        }
        else {
            super();
        
            this._values = {};
            this._isValuesDefined = true;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return [ ...super.getPropertyNames(), ...StaticEngine.propertyNames ];
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [ ...super.getDefinedPropertyNames() ];
        
        if (this.isValuesDefined && this.values !== undefined)
            definedProperties.push('values');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'values':
                return this.values;
            default:
                return super.getPropertyValue(propertyName);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        super.write(writer);
        
        if (this.isValuesDefined && this.values !== undefined) {
            if (this.values !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('values', 'dictionary'));
        
                // Implementation: axon/write/dictionary
                writer.writeDictionaryHeader(new Axon.DictionaryHeader(_.keys(this.values).length));
                for (let key in this.values) {
                    const dictionaryValue = this.values[key];
                
                    writer.writeDictionaryItemHeader(new Axon.DictionaryItemHeader('string', dictionaryValue !== null ? 'string' : 'null'));
                
                    // Key Implementation: axon/write/string
                    writer.writeStringValue(key);
                
                    if (dictionaryValue !== null) {
                        // Implementation: axon/write/string
                        writer.writeStringValue(dictionaryValue);
                    }
                }
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('values', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'values':
                // Implementation: axon/readProperty/dictionary
                if (propertyHeader.type === 'dictionary') {
                    const dictionaryHeader = reader.readDictionaryHeader();
                
                    this.values = {};
                
                    for (let i = 0; i < dictionaryHeader.recordCount; i++) {
                        let key: string = <any>undefined;
                        let value: string = <any>undefined;
                
                        const dictionaryItemHeader = reader.readDictionaryItemHeader();
                
                        // Key Implementation: string
                        if (dictionaryItemHeader.keyType === 'string') {
                            key = reader.readStringValue();
                        }
                        else if (dictionaryItemHeader.keyType === 'null') {
                            // Do Nothing - TODO Review
                            // throw new Error(`Value for property 'values' cannot be null`);
                            key = <any>null;
                        }
                        else {
                            throw new Error(`Unexpected data type '${dictionaryItemHeader.keyType}' for property 'values'`);
                        }
                
                        // Value Implementation: string
                        if (dictionaryItemHeader.valueType === 'string') {
                            value = reader.readStringValue();
                        }
                        else if (dictionaryItemHeader.valueType === 'null') {
                            // Do Nothing - TODO Review
                            // throw new Error(`Value for property 'values' cannot be null`);
                            value = <any>null;
                        }
                        else {
                            throw new Error(`Unexpected data type '${dictionaryItemHeader.valueType}' for property 'values'`);
                        }
                
                        this.values[key] = value;
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'values' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'values'`);
                }
        
                return true;
            default:
                return super.readProperty(reader, propertyHeader);
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        super.slipstreamWrite(writer);
        
        // values
        if (this.isValuesDefined && this.values !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/dictionary
            if (this.values === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                writer.writeIntegerValue(_.keys(this.values).length);
            
                for (let key in this.values) {
                    // Key Implementation: string
                    if (key === null)
                    {
                        writer.writeByteValue(0);
                    }
                    else
                    {
                        writer.writeByteValue(1);
                        writer.writeStringValue(key);
                    }
            
                    // Value Implementation: string
                    if (this.values[key] === null)
                    {
                        writer.writeByteValue(0);
                    }
                    else
                    {
                        writer.writeByteValue(1);
                        writer.writeStringValue(this.values[key]);
                    }
                }
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        super.slipstreamRead(reader);
        
        // values
        if (reader.readBooleanValue())
        {
            // Implementation: dictionary
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null dictionaries');
                    break;
                case 1:
                    let key: string;
                    let value: string;
            
                    this.values = {};
                    
                    const length = reader.readIntegerValue();
                    for (let i = 0; i < length; i++) {
                        // Key Implementation: string
                        switch (reader.readByteValue())
                        {
                            case 0:
                                key = <any>null;
                                break;
                            case 1:
                                key = reader.readStringValue();
                                break;
                            default:
                                throw new Error('Invalid null code');
                        }
            
                        // Value Implementation: string
                        switch (reader.readByteValue())
                        {
                            case 0:
                                value = <any>null;
                                break;
                            case 1:
                                value = reader.readStringValue();
                                break;
                            default:
                                throw new Error('Invalid null code');
                        }
            
                        this.values[key] = value;
                    }
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): StaticEngine {
        // Method Implementation: simple
        const schema = StaticEngineZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: StaticEngineZodSchemaShape): StaticEngine {
        // Method Implementation: parseZodSchema
        return new StaticEngine({
            values: schema.values
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            engineType: 'static',
            ...super.toJSON(),
            values: this.isValuesDefined ? (this.values) : undefined,
        };
    }

    public clone(): StaticEngine {
        // Method Implementation: clone
        return new StaticEngine({
            values: this.isValuesDefined ? this.values : undefined
        });
    }
}

export class AssignedValueEngine extends AEngine implements IAssignedValueEngine {
    public static readonly modelName: string = 'AssignedValueEngine';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'variables' ];

    public get modelName(): string {
        // Method Implementation: simple
        return AssignedValueEngine.modelName;
    }

    private _variables: string[];

    public get variables(): string[] {
        // Method Implementation: simple
        return this._variables;
    }

    public set variables(value: string[]) {
        // Method Implementation: simple
        this._variables = value;
        this._isVariablesDefined = true;
    }

    private _isVariablesDefined: boolean;

    public get isVariablesDefined(): boolean {
        // Method Implementation: simple
        return this._isVariablesDefined;
    }

    public constructor();
    public constructor(source: IAssignedValueEngine);
    public constructor(source: Partial<Pick<AssignedValueEngine, 'variables'>>);
    public constructor(source?: IAssignedValueEngine | Partial<Pick<AssignedValueEngine, 'variables'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            super(source);
        
            if (isAssignedValueEngineModel(source)) {
                if (source.isVariablesDefined) {
                    this._variables = source.variables;
                    this._isVariablesDefined = true;
                }
                else {
                    this._variables = [];
                    this._isVariablesDefined = true;
                }
            }
            else {
                if (source.variables !== undefined) {
                    this._variables = source.variables;
                    this._isVariablesDefined = true;
                }
                else {
                    this._variables = [];
                    this._isVariablesDefined = true;
                }
            }
        }
        else {
            super();
        
            this._variables = [];
            this._isVariablesDefined = true;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return [ ...super.getPropertyNames(), ...AssignedValueEngine.propertyNames ];
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [ ...super.getDefinedPropertyNames() ];
        
        if (this.isVariablesDefined && this.variables !== undefined)
            definedProperties.push('variables');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'variables':
                return this.variables;
            default:
                return super.getPropertyValue(propertyName);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        super.write(writer);
        
        if (this.isVariablesDefined && this.variables !== undefined) {
            if (this.variables !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('variables', 'array'));
        
                // Implementation: axon/write/array
                writer.writeArrayHeader(new Axon.ArrayHeader(this.variables.length));
                for (let i = 0; i < this.variables.length; i++) {
                    const item = this.variables[i];
                
                    if (item !== null) {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('string'));
                
                        // Implementation: axon/write/string
                        writer.writeStringValue(item);
                    }
                    else {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('null'));
                    }
                }
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('variables', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'variables':
                // Implementation: axon/readProperty/array
                if (propertyHeader.type === 'array') {
                    const arrayHeader = reader.readArrayHeader();
                
                    this.variables = [];
                
                    let item: string = <any>undefined;
                    for (let i = 0; i < arrayHeader.itemCount; i++) {
                        const arrayItemHeader = reader.readArrayItemHeader();
                
                        // Implementation: string
                        if (arrayItemHeader.type === 'string') {
                            item = reader.readStringValue();
                        }
                        else if (arrayItemHeader.type === 'null') {
                            // Do Nothing - TODO Review
                            // throw new Error(`Value for property 'variables' cannot be null`);
                            item = <any>null;
                        }
                        else {
                            throw new Error(`Unexpected data type '${arrayItemHeader.type}' for property 'variables'`);
                        }
                
                        this.variables.push(item);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'variables' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'variables'`);
                }
        
                return true;
            default:
                return super.readProperty(reader, propertyHeader);
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        super.slipstreamWrite(writer);
        
        // variables
        if (this.isVariablesDefined && this.variables !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/array
            if (this.variables === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                writer.writeIntegerValue(this.variables.length);
            
                for (const item of this.variables) {
                    // Item Implementation: string
                    if (item === null)
                    {
                        writer.writeByteValue(0);
                    }
                    else
                    {
                        writer.writeByteValue(1);
                        writer.writeStringValue(item);
                    }
                }
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        super.slipstreamRead(reader);
        
        // variables
        if (reader.readBooleanValue())
        {
            // Implementation: array
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null arrays');
                    break;
                case 1:
                    let item: string;
            
                    this.variables = [];
                    
                    const length = reader.readIntegerValue();
                    for (let i = 0; i < length; i++) {
                        // Item Implementation: string
                        switch (reader.readByteValue())
                        {
                            case 0:
                                item = <any>null;
                                break;
                            case 1:
                                item = reader.readStringValue();
                                break;
                            default:
                                throw new Error('Invalid null code');
                        }
            
                        this.variables.push(item);
                    }
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): AssignedValueEngine {
        // Method Implementation: simple
        const schema = AssignedValueEngineZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: AssignedValueEngineZodSchemaShape): AssignedValueEngine {
        // Method Implementation: parseZodSchema
        return new AssignedValueEngine({
            variables: schema.variables?.map(i => i)
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            engineType: 'assignedValue',
            ...super.toJSON(),
            variables: this.isVariablesDefined ? (this.variables?.map(item => (item === null ? undefined : item))) : undefined,
        };
    }

    public clone(): AssignedValueEngine {
        // Method Implementation: clone
        return new AssignedValueEngine({
            variables: this.isVariablesDefined ? this.variables?.map(i => i) : undefined
        });
    }
}

export class ModelClassFactory {
    public static createModel(modelName: string) {
        // Method Implementation: createModel
        switch (modelName) {
            case 'ConstraintModel':
                return new ConstraintModel();
            case 'ConfigitEngine':
                return new ConfigitEngine();
            case 'ConfigitEngineDependencyMapping':
                return new ConfigitEngineDependencyMapping();
            case 'MapperEngine':
                return new MapperEngine();
            case 'ReflectionEngine':
                return new ReflectionEngine();
            case 'FanSelectionEngine':
                return new FanSelectionEngine();
            case 'MacroEngine':
                return new MacroEngine();
            case 'MacroEngineValue':
                return new MacroEngineValue();
            case 'RecommendationEngine':
                return new RecommendationEngine();
            case 'StaticEngine':
                return new StaticEngine();
            case 'AssignedValueEngine':
                return new AssignedValueEngine();
            default:
                throw new Error(`Unknown model ${modelName}`);
        }
    }

    public static createFromModel(modelName: string, source: any) {
        // Method Implementation: createFromModel
        switch (modelName) {
            case 'ConstraintModel':
                return new ConstraintModel(source);
            case 'ConfigitEngine':
                return new ConfigitEngine(source);
            case 'ConfigitEngineDependencyMapping':
                return new ConfigitEngineDependencyMapping(source);
            case 'MapperEngine':
                return new MapperEngine(source);
            case 'ReflectionEngine':
                return new ReflectionEngine(source);
            case 'FanSelectionEngine':
                return new FanSelectionEngine(source);
            case 'MacroEngine':
                return new MacroEngine(source);
            case 'MacroEngineValue':
                return new MacroEngineValue(source);
            case 'RecommendationEngine':
                return new RecommendationEngine(source);
            case 'StaticEngine':
                return new StaticEngine(source);
            case 'AssignedValueEngine':
                return new AssignedValueEngine(source);
            default:
                throw new Error(`Unknown model ${modelName}`);
        }
    }

    public static parseModel(modelName: string, source: any) {
        // Method Implementation: parseModel
        switch (modelName) {
            case 'ConstraintModel':
                return ConstraintModel.parse(source);
            case 'ConfigitEngine':
                return ConfigitEngine.parse(source);
            case 'ConfigitEngineDependencyMapping':
                return ConfigitEngineDependencyMapping.parse(source);
            case 'MapperEngine':
                return MapperEngine.parse(source);
            case 'ReflectionEngine':
                return ReflectionEngine.parse(source);
            case 'FanSelectionEngine':
                return FanSelectionEngine.parse(source);
            case 'MacroEngine':
                return MacroEngine.parse(source);
            case 'MacroEngineValue':
                return MacroEngineValue.parse(source);
            case 'RecommendationEngine':
                return RecommendationEngine.parse(source);
            case 'StaticEngine':
                return StaticEngine.parse(source);
            case 'AssignedValueEngine':
                return AssignedValueEngine.parse(source);
            default:
                throw new Error(`Unknown model ${modelName}`);
        }
    }
}
